import React, { useState, useEffect, useRef, useCallback } from "react";
import { productsAPI } from "../services/api";
import "./ProductSearchModal.css";

/**
 * ProductSearchModal — Modal de búsqueda avanzada de productos tipo farmacia.
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - onSelectProduct: (product, presentacion, cantidad) => void  ← opcional
 *  - sucursalId: number | null
 */
const ProductSearchModal = ({ isOpen, onClose, onSelectProduct, sucursalId = null }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchMode, setSearchMode] = useState("nombre");
    const [results, setResults] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedPresentacion, setSelectedPresentacion] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [cantidad, setCantidad] = useState(1);

    const searchRef = useRef(null);
    const debounceTimer = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setSearchTerm("");
            setResults([]);
            setSelectedProduct(null);
            setSelectedPresentacion(null);
            setCantidad(1);
            setTimeout(() => searchRef.current?.focus(), 100);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (e.altKey && e.key.toLowerCase() === "b") { e.preventDefault(); setSearchMode("nombre"); searchRef.current?.focus(); }
            if (e.altKey && e.key.toLowerCase() === "r") { e.preventDefault(); setSearchMode("codigo"); searchRef.current?.focus(); }
            if (e.altKey && e.key.toLowerCase() === "e") { e.preventDefault(); setSearchMode("principio"); searchRef.current?.focus(); }
            if (e.altKey && e.key.toLowerCase() === "l") { e.preventDefault(); setSearchMode("laboratorio"); searchRef.current?.focus(); }
            if (e.altKey && e.key.toLowerCase() === "a") { e.preventDefault(); handleAgregarPedido(); }
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [isOpen, selectedProduct, selectedPresentacion, cantidad, onClose]);

    const doSearch = useCallback(async (term) => {
        if (!term || term.trim().length < 2) { setResults([]); return; }
        setIsLoading(true);
        try {
            const res = await productsAPI.search(term.trim(), sucursalId);
            let products = res.data?.data?.products || [];
            if (searchMode === "codigo") {
                products = products.filter(p => p.code?.toLowerCase().includes(term.toLowerCase()));
            } else if (searchMode === "principio") {
                products = products.filter(p => p.principio_activo?.toLowerCase().includes(term.toLowerCase()));
            } else if (searchMode === "laboratorio") {
                products = products.filter(p => p.laboratorio?.toLowerCase().includes(term.toLowerCase()));
            } else if (searchMode === "barras") {
                products = products.filter(p => p.codigo_barras?.toLowerCase().includes(term.toLowerCase()));
            }
            setResults(products);
        } catch (err) {
            console.error("Error búsqueda:", err);
        } finally {
            setIsLoading(false);
        }
    }, [searchMode, sucursalId]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => doSearch(val), 350);
    };

    const handleSelectProduct = (product) => {
        setSelectedProduct(product);
        if (product.presentations?.length > 0) {
            setSelectedPresentacion(product.presentations[0]);
        } else {
            setSelectedPresentacion(null);
        }
    };

    const handleAgregarPedido = () => {
        if (!selectedProduct || !onSelectProduct) return;
        onSelectProduct(selectedProduct, selectedPresentacion, parseInt(cantidad) || 1);
    };

    const getUnitPrice = () => {
        if (!selectedProduct) return 0;
        return parseFloat(selectedPresentacion?.price || selectedProduct.sale_price || 0);
    };

    const montoTotal = () => (getUnitPrice() * (parseInt(cantidad) || 1)).toFixed(2);

    if (!isOpen) return null;

    return (
        <div className="psm-overlay" onClick={onClose}>
            <div className="psm-modal" onClick={e => e.stopPropagation()}>

                {/* HEADER */}
                <div className="psm-header">
                    <div className="psm-header-title">
                        <span className="psm-icon">🔍</span>
                        <h2>Búsqueda de Productos</h2>
                    </div>
                    <button className="psm-close" onClick={onClose}>✕ Salir (ESC)</button>
                </div>

                {/* BARRA DE BÚSQUEDA */}
                <div className="psm-search-bar">
                    <div className="psm-mode-tabs">
                        <button className={`psm-tab ${searchMode === "nombre" ? "active" : ""}`} onClick={() => { setSearchMode("nombre"); searchRef.current?.focus(); }}>
                            Nombre <kbd>ALT+B</kbd>
                        </button>
                        <button className={`psm-tab ${searchMode === "codigo" ? "active" : ""}`} onClick={() => { setSearchMode("codigo"); searchRef.current?.focus(); }}>
                            Código SKU <kbd>ALT+R</kbd>
                        </button>
                        <button className={`psm-tab ${searchMode === "principio" ? "active" : ""}`} onClick={() => { setSearchMode("principio"); searchRef.current?.focus(); }}>
                            Principio Activo <kbd>ALT+E</kbd>
                        </button>
                        <button className={`psm-tab ${searchMode === "laboratorio" ? "active" : ""}`} onClick={() => { setSearchMode("laboratorio"); searchRef.current?.focus(); }}>
                            Laboratorio <kbd>ALT+L</kbd>
                        </button>
                        <button className={`psm-tab ${searchMode === "barras" ? "active" : ""}`} onClick={() => { setSearchMode("barras"); searchRef.current?.focus(); }}>
                            ▌▌▌ Cód. Barras
                        </button>
                    </div>
                    <div className="psm-input-row">
                        <input
                            ref={searchRef}
                            type="text"
                            className="psm-input"
                            placeholder={
                                searchMode === "nombre"      ? "Escribir nombre del medicamento..." :
                                searchMode === "codigo"      ? "Escribir código SKU interno..." :
                                searchMode === "principio"   ? "Escribir principio activo (INN)..." :
                                searchMode === "laboratorio" ? "Escribir nombre de laboratorio..." :
                                searchMode === "barras"      ? "Escanear o escribir código de barras EAN-13..." :
                                "Buscar..."
                            }
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                        {isLoading && <span className="psm-loading-dot">⟳</span>}
                        {searchTerm && (
                            <button className="psm-clear" onClick={() => { setSearchTerm(""); setResults([]); searchRef.current?.focus(); }}>
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                {/* TABLA DE RESULTADOS */}
                <div className="psm-results-section">
                    <table className="psm-table">
                        <thead>
                            <tr>
                                <th>Producto</th>
                                <th>Stock</th>
                                <th>Concentración</th>
                                <th>Presentación</th>
                                <th>Fracc.</th>
                                <th>Titular / Lab.</th>
                                <th>Cód. Barras</th>
                                <th>S/ Venta</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.length === 0 && searchTerm.length >= 2 && !isLoading && (
                                <tr><td colSpan={7} className="psm-empty">No se encontraron productos</td></tr>
                            )}
                            {results.length === 0 && searchTerm.length < 2 && (
                                <tr><td colSpan={7} className="psm-empty psm-hint">Escribe al menos 2 caracteres para buscar...</td></tr>
                            )}
                            {results.map(product => {
                                const isSelected = selectedProduct?.id === product.id;
                                const rows = product.presentations?.length > 0
                                    ? product.presentations
                                    : [{ id: "base", name: product.unit || "Unidad", factor: 1, price: product.sale_price }];

                                return rows.map((pres, idx) => (
                                    <tr
                                        key={`${product.id}-${pres.id || idx}`}
                                        className={`psm-row ${isSelected ? "selected" : ""} ${product.stock <= 0 ? "agotado" : ""}`}
                                        onClick={() => handleSelectProduct(product)}
                                    >
                                        {idx === 0 && (
                                            <td rowSpan={rows.length} className="psm-td-name">
                                                <div className="psm-product-name">{product.name}</div>
                                                <div className="psm-product-code">{product.code}</div>
                                                {product.stock <= 0 && <span className="psm-badge-agotado">AGOTADO</span>}
                                            </td>
                                        )}
                                        {idx === 0 && <td rowSpan={rows.length} className="psm-td-center">{product.stock}</td>}
                                        {idx === 0 && <td rowSpan={rows.length} className="psm-td-center psm-concentracion">{product.concentracion || "—"}</td>}
                                        <td className="psm-td-presentacion">{pres.name}</td>
                                        <td className="psm-td-center">{pres.factor}</td>
                                        {idx === 0 && <td rowSpan={rows.length} className="psm-td-lab">{product.laboratorio || "—"}</td>}
                                        {idx === 0 && <td rowSpan={rows.length} className="psm-td-center" style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{product.codigo_barras || "—"}</td>}
                                        <td className={`psm-td-price ${product.stock <= 0 ? "price-muted" : ""}`}>
                                            {product.stock <= 0 ? "—" : `S/. ${parseFloat(pres.price || product.sale_price).toFixed(2)}`}
                                        </td>
                                    </tr>
                                ));
                            })}
                        </tbody>
                    </table>
                </div>

                {/* SECCIÓN INFERIOR */}
                <div className="psm-bottom">
                    <div className="psm-precios-section">
                        <div className="psm-precios-header">Precios del Producto Seleccionado</div>
                        {selectedProduct ? (
                            <div className="psm-precios-table-wrap">
                                <table className="psm-precios-table">
                                    <thead>
                                        <tr>
                                            <th>Precio</th>
                                            <th>Dsc.%</th>
                                            <th>Modalidad</th>
                                            <th>Cantid.</th>
                                            <th>S/Dsc</th>
                                            <th>Bonif</th>
                                            <th>Efectivo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(selectedProduct.presentations?.length > 0
                                            ? selectedProduct.presentations
                                            : [{ name: "Base", factor: 1, price: selectedProduct.sale_price }]
                                        ).map((pres, i) => (
                                            <tr
                                                key={i}
                                                className={`psm-precio-row ${selectedPresentacion?.name === pres.name ? "selected-pres" : ""}`}
                                                onClick={() => setSelectedPresentacion(pres)}
                                            >
                                                <td className="psm-precio-val">S/. {parseFloat(pres.price || selectedProduct.sale_price).toFixed(2)}</td>
                                                <td>0%</td>
                                                <td>{pres.name}</td>
                                                <td>{pres.factor}</td>
                                                <td>—</td>
                                                <td>—</td>
                                                <td>S/. {parseFloat(pres.price || selectedProduct.sale_price).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="psm-precios-empty">Selecciona un producto para ver sus precios</div>
                        )}
                    </div>

                    <div className="psm-calc-section">
                        <div className="psm-calc-grid">
                            <div className="psm-calc-field">
                                <label>Precio Unitario</label>
                                <input type="text" readOnly value={selectedProduct ? `S/. ${getUnitPrice().toFixed(2)}` : ""} className="psm-calc-input" />
                            </div>
                            <div className="psm-calc-field">
                                <label>Cantidad</label>
                                <input type="number" min="1" value={cantidad} onChange={e => setCantidad(e.target.value)} className="psm-calc-input" />
                            </div>
                            <div className="psm-calc-field">
                                <label>Costo Total</label>
                                <input type="text" readOnly value={selectedProduct ? `S/. ${montoTotal()}` : ""} className="psm-calc-input psm-calc-total" />
                            </div>
                        </div>
                        <div className="psm-calc-actions">
                            <button className="psm-btn psm-btn-calcular" disabled={!selectedProduct}>
                                📊 Calcular Monto <kbd>ALT+M</kbd>
                            </button>
                            <button className="psm-btn psm-btn-agregar" disabled={!selectedProduct || !onSelectProduct} onClick={handleAgregarPedido}>
                                ➕ Agregar al Pedido <kbd>ALT+A</kbd>
                            </button>
                        </div>
                        <div className="psm-importe-total">
                            <span className="psm-importe-label">Importe Total</span>
                            <span className="psm-importe-val">{selectedProduct ? `S/. ${montoTotal()}` : "—"}</span>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default ProductSearchModal;
